import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCluster;

export type DbClusterDetailsQueryData = ApiResponse<typeof apiFn>;

export type DbClusterDetailsQueryArgs<SELECT> = QueryOptions<
  DbClusterDetailsQueryData,
  SELECT
> & {
  clusterId?: string;
  key: string;
  getRange: () => TimeRange;
};

const DBO_CLUSTER_DETAILS_QUERY_KEY = 'dbo-cluster-details';

const getDbClusterDetailsQueryKey = ({
  clusterId,
  key,
}: { clusterId?: string; key?: string } = {}) =>
  [DBO_CLUSTER_DETAILS_QUERY_KEY, clusterId, key] as const;

const getDbClusterDetailsQueryOptions = ({
  clusterId,
  key,
  getRange,
  enabled,
}: {
  clusterId?: string;
  key: string;
  getRange: () => TimeRange;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getDbClusterDetailsQueryKey({ clusterId, key }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        clusterId: clusterId!,
        startTime: range.from,
        endTime: range.to,
      });
      return data;
    },
    enabled: makeIsEnabled(!!clusterId, enabled),
  });

export const useDbClusterDetailsQuery = <SELECT = DbClusterDetailsQueryData>({
  clusterId,
  key,
  getRange,
  enabled,
  select,
}: DbClusterDetailsQueryArgs<SELECT>) => {
  return useQuery({
    ...getDbClusterDetailsQueryOptions({
      clusterId,
      key,
      getRange,
      enabled,
    }),
    select,
  });
};
